import React from 'react';
import {
  Autocomplete,
  SxProps,
  TextField,
  AutocompleteRenderGetTagProps,
  AutocompleteOwnerState
} from '@mui/material';
import { Colors } from '../../../utils/theme';

type Data = any;

interface IProps {
  id: string;
  label: string;
  values: Data | Data[];
  onChange: (event: unknown, newValue: Data | Data[] | null) => void;
  options: Data[];
  getOptionLabel?: (value: Data) => string;
  isSingle?: boolean;
  errorMsg?: string | null;
  additionalStyle?: SxProps;
  disabled?: boolean;
  isWhite?: boolean;
  startAdornment?: React.ReactNode;
  renderTags?: (
    value: any[],
    getTagProps: AutocompleteRenderGetTagProps,
    ownerState: AutocompleteOwnerState<any, boolean, undefined, undefined, 'div'>
  ) => React.ReactNode;
  isOptionEqualToValue?: (option: any, value: any) => boolean;
}

const AutocompleteComponent: React.FunctionComponent<IProps> = ({
  id,
  label,
  values,
  onChange,
  options,
  isSingle = true,
  errorMsg,
  additionalStyle,
  disabled = false,
  isWhite = false,
  getOptionLabel,
  startAdornment,
  renderTags,
  isOptionEqualToValue
}) => {
  return (
    <Autocomplete
      id={`${id}-autocomplete`}
      data-testid={`${id}-autocomplete`}
      multiple={isSingle === true ? false : true}
      value={values}
      fullWidth
      {...(getOptionLabel && { getOptionLabel })}
      filterSelectedOptions
      onChange={onChange}
      disabled={disabled}
      renderOption={(props, option) => {
        // Use option.id if available, otherwise use a combination of props
        const key = option?.id || option?.name || (typeof option === 'string' ? option : props.id);
        return (
          <li {...props} key={key}>
            {getOptionLabel ? getOptionLabel(option) : String(option)}
          </li>
        );
      }}
      options={options}
      sx={{ minHeight: '36px', padding: 0, ...additionalStyle }}
      {...(isOptionEqualToValue && { isOptionEqualToValue })}
      renderInput={(params) => (
        <TextField
          {...params}
          id={id}
          label={label}
          disabled={disabled}
          {...(errorMsg !== undefined && {
            error: errorMsg !== null,
            helperText: errorMsg ?? '',
            required: true
          })}
          sx={{
            color: errorMsg !== undefined && errorMsg !== null ? Colors.red : Colors.main,
            backgroundColor: isWhite ? Colors.white : Colors.input_field_background,
            minHeight: '36px',
            ...(isSingle && {
              maxHeight: '36px'
            }),
            borderRadius: '9px',
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                border: 'none'
              }
            }
          }}
          InputProps={{
            ...params.InputProps,
            sx: {
              paddingY: '0 !important',
              position: 'relative',
              paddingLeft: `${startAdornment ? '5' : '12'}px !important`
            },
            startAdornment: (
              <>
                {startAdornment}
                {params.InputProps.startAdornment}
              </>
            )
          }}
          FormHelperTextProps={{
            sx: {
              marginX: 0
            }
          }}
          InputLabelProps={{
            sx: {
              lineHeight: '22px',
              top: '-9px',
              color: errorMsg !== undefined && errorMsg !== null ? Colors.red : Colors.main
            }
          }}
          inputProps={{
            ...params.inputProps,
            sx: {
              height: '36px',
              padding: '0 !important',
              color: errorMsg !== undefined && errorMsg !== null ? Colors.red : Colors.main
            }
          }}
        />
      )}
      renderTags={renderTags}
    />
  );
};

export default AutocompleteComponent;
